<template>
  <div :class="[$options.name, statusClassList]" :data-e-ga-id="uniqueId">
    <div v-if="label || $slots.tooltip" class="tw:flex tw:gap-1 tw:items-center">
      <label v-if="label" :class="['tw:body-1 ', $options.name + '__label']">
        {{ label }}
      </label>
      <!--  @slot This slot is for the tooltip component.  -->
      <slot name="tooltip" />
    </div>
    <multiselect
      v-model="value"
      :allow-empty="allowEmpty"
      :disabled="disabled"
      :loading="loading"
      :max-height="maxHeight"
      :multiple="multiple"
      :options="options"
      :placeholder="placeholder"
      :searchable="searchable"
      :tag-placeholder="''"
      :taggable="taggable"
      :internal-search="internalSearch"
      label="name"
      track-by="value"
      @input="(value) => $emit('change', value)"
      @remove="(value) => $emit('remove', value)"
      @select="(value) => $emit('select', value)"
      @tag="(value) => $emit('add', value)"
      @search-change="searchFunction"
    >
      <template v-slot:noResult>No matching options were found</template>
    </multiselect>

    <div v-if="!disabled && invalid && errorMsg" class="tw:caption tw:!text-red-500">
      {{ errorMsg }}
    </div>

    <div v-else-if="helper" class="tw:caption tw:!text-default-700">
      {{ helper }}
    </div>
  </div>
</template>

<script lang="ts">
import multiselect from "vue-multiselect";
import { defineComponent, ref, computed, watch } from "vue";
import type { PropType } from "vue";
import { gtagUniversalSend } from "vue-app/plugins/edroneGtag";

// TODO: tmp solution. Need to rewrite this component to fix this https://edrone.atlassian.net/browse/COOLD-919
export type EdroneSelectOption<T = any> = {
  name: string;
  value: T;
};

export default defineComponent({
  name: "EdroneSelect",
  components: {
    multiselect,
  },
  props: {
    /**
     * selectedOptions can be an array when multiple is true, or an object when multiple is false
     */
    selectedOptions: {
      type: [Array, Object] as PropType<EdroneSelectOption[] | EdroneSelectOption>,
      default: () => [],
    },
    /**
     * The label will be displayed above the input with the provided value
     */
    label: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * If true, the input will be a multiselect
     */
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    placeholder: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * If true, the user will be able to add a new option.
     * The event "add" will be emitted with the value of the input
     */
    taggable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Options can be an array of objects with name and value properties, or an array of strings
     */
    options: {
      type: Array as PropType<EdroneSelectOption[]>,
      required: true,
    },
    /**
     * If true, you can search for options with keyboard
     */
    searchable: {
      type: Boolean,
      required: false,
      default: false,
    },
    allowEmpty: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Max height of the dropdown
     */
    maxHeight: {
      type: Number,
      required: false,
      default: 300,
    },
    /**
     * Error state of the input
     */
    invalid: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The text to be displayed below the input when the `invalid` prop is set to `true`.
     */
    errorMsg: {
      required: false,
      type: String,
      default: null,
    },
    /**
     * Text to display below the input
     */
    helper: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Function to be called when the user types in the input.
     * The function will be called with the value of the input as the first argument.
     */
    searchFunction: {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      type: Function as PropType<(query: string) => void>,
      required: false,
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      default: () => {},
    },
    /**
     * If true, the loading spinner will be displayed
     */
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    uniqueId: {
      type: String,
      default: "",
    },
    /**
     * Internal search, disable if accesing api for new values
     */
    internalSearch: {
      type: Boolean,
      default: true,
    },
  },
  emits: {
    /**
     * Emitted after selecting an option
     */
    select: (option: EdroneSelectOption) => !!option,
    /**
     * Emitted after removing an option
     */
    remove: (option: EdroneSelectOption) => !!option,
    /**
     * Emitted after changing the input value. Emitted value is `InputEvent`
     */
    change: (option: EdroneSelectOption) => !!option,
    /**
     * Emitted after user attempts to add an option.
     * The `taggable` prop must be true for this event to be emitted
     */
    add: (option: EdroneSelectOption) => !!option,
  },
  setup(props) {
    const value = ref(props.selectedOptions);

    const statusClassList = computed(() => {
      return {
        "EdroneSelect--invalid": props.invalid,
        "EdroneSelect--disabled": props.disabled,
      };
    });

    watch(
      () => props.selectedOptions,
      () => {
        value.value = props.selectedOptions;
        gtagUniversalSend({ component_id: props.uniqueId });
      }
    );

    return {
      value,
      statusClassList,
    };
  },
  methods: { gtagUniversalSend },
});
</script>

<style>
@reference "vue-app/assets/scss/main.css";

.EdroneSelect {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.EdroneSelect__label {
  display: block;
}

.EdroneSelect .multiselect {
  box-sizing: content-box;
  display: block;
  position: relative;
  width: 100%;
  min-height: 40px;
  outline: none;
}

.EdroneSelect .multiselect .multiselect__select {
  position: absolute;
  width: 40px;
  height: 38px;
  right: 1px;
  top: 1px;
  padding: 4px 8px;
  text-align: center;
  transition: transform 0.2s ease;
}

.EdroneSelect .multiselect .multiselect__tags {
  min-height: 40px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  justify-content: center;
  padding: 4px 40px 4px 4px;
}
.EdroneSelect .multiselect .multiselect__tags {
  @apply tw:text-sm tw:border tw:border-default-200 tw:rounded-sm tw:bg-white;
}

.EdroneSelect .multiselect .multiselect__tags:hover {
  @apply tw:border tw:border-default-400;
}

.EdroneSelect .multiselect .multiselect__placeholder {
  padding: 0 4px;
  @apply tw:text-default-600 tw:text-sm tw:leading-5;
}

.EdroneSelect .multiselect input::placeholder {
  /* Chrome, Firefox, Opera, Safari 10.1+ */
  opacity: 1; /* Firefox */
  font-family: "Roboto", sans-serif;
  @apply tw:text-default-600;
}

.EdroneSelect .multiselect input:-ms-input-placeholder {
  /* Internet Explorer 10-11 */
  font-family: "Roboto", sans-serif;
  @apply tw:text-default-600;
}

.EdroneSelect .multiselect input::-ms-input-placeholder {
  /* Microsoft Edge */
  font-family: "Roboto", sans-serif;
  @apply tw:text-default-600;
}

.EdroneSelect .multiselect .multiselect__tags-wrap {
  display: flex;
  flex-wrap: wrap !important;
  gap: 4px;
}

.EdroneSelect .multiselect .multiselect__spinner {
  position: absolute;
  right: 1px;
  top: 1px;
  width: 40px;
  height: 38px;
  display: block;
  background: white;
}

.EdroneSelect .multiselect .multiselect__input,
.EdroneSelect .multiselect .multiselect__single {
  position: relative;
  display: inline-block;
  min-height: 20px;
  line-height: 20px;
  border: none;
  border-radius: 4px;
  padding: 0 4px;
  width: 100%;
  transition: border 0.1s ease;
  box-sizing: border-box;
  vertical-align: top;
  outline: none;
}

.EdroneSelect .multiselect .multiselect__single {
  overflow: hidden;
  text-overflow: ellipsis;
  @apply tw:!text-black tw:!text-sm tw:!leading-5;
}

.EdroneSelect .multiselect .multiselect__tag {
  padding: 4px 6px;
  @apply tw:bg-blue-000;
  border: 1px solid;
  @apply tw:border-blue-500;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  cursor: default;
  user-select: none;
  @apply tw:!text-black;
  @apply tw:leading-5;
  @apply tw:text-sm;
}

.EdroneSelect .multiselect .multiselect__content-wrapper {
  position: absolute;
  display: block;
  background: #fff;
  max-height: 240px;
  overflow: auto;
  border-radius: 4px;
  z-index: 33;
  -webkit-overflow-scrolling: touch;
  margin-top: 8px;
  box-shadow: 4px 4px 24px 0px #00000038;
  width: 100%;
}

.EdroneSelect .multiselect .multiselect__content {
  list-style: none;
  display: inline-block;
  padding: 0;
  margin: 0;
  min-width: 100%;
  vertical-align: top;
}

.EdroneSelect .multiselect .multiselect__tag-icon {
  height: 16px;
  width: 16px;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  font-style: normal !important;
}

.EdroneSelect .multiselect .multiselect__tag-icon:hover {
  @apply tw:bg-blue-300;
  border-radius: 4px;
}

.EdroneSelect .multiselect .multiselect__tag-icon:after {
  content: "\D7";
  font-size: 14px;
  @apply tw:text-black;
}

.EdroneSelect .multiselect .multiselect__element > span {
  padding: 14px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  width: 100%;
  display: inline-flex;
  align-items: center;
  padding-right: 32px;
}

.EdroneSelect .multiselect .multiselect__option {
  padding: 14px;
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  width: 100%;
  display: inline-flex;
  align-items: center;
}

.EdroneSelect .multiselect .multiselect__option--highlight {
  @apply tw:bg-default-000;
}

.EdroneSelect .multiselect .multiselect__option--selected span {
  @apply tw:text-blue-500;
}

.EdroneSelect .multiselect .multiselect__option--selected:after {
  content: "✓";
  @apply tw:text-blue-500;
  position: absolute;
  right: 10px;
}

/* arrows */

.EdroneSelect .multiselect .multiselect__select:before {
  position: relative;
  right: 0;
  top: 65%;
  margin-top: 4px;
  border-style: solid;
  border-width: 5px 5px 0;
  content: "";
  @apply tw:border-t-default-700 tw:border-transparent tw:text-default-700;
}

.EdroneSelect .multiselect .multiselect__current,
.EdroneSelect .multiselect .multiselect__select {
  line-height: 16px;
  box-sizing: border-box;
  display: block;
  margin: 0;
  text-decoration: none;
  cursor: pointer;
}

/* spinner */

@keyframes a {
  0% {
    transform: rotate(0);
  }

  to {
    transform: rotate(2turn);
  }
}

.EdroneSelect .multiselect .multiselect__spinner:after,
.EdroneSelect .multiselect .multiselect__spinner:before {
  position: absolute;
  content: "";
  top: 50%;
  left: 50%;
  margin: -8px 0 0 -8px;
  width: 16px;
  height: 16px;
  border-radius: 100%;
  @apply tw:border-blue-500;
  border-style: solid;
  border-width: 2px;
  box-shadow: 0 0 0 1px transparent;
}

.EdroneSelect .multiselect .multiselect__spinner:before {
  animation: a 2.4s cubic-bezier(0.41, 0.26, 0.2, 0.62);
  animation-iteration-count: infinite;
}

.EdroneSelect .multiselect .multiselect__spinner:after {
  animation: a 2.4s cubic-bezier(0.51, 0.09, 0.21, 0.8);
  animation-iteration-count: infinite;
}

/* tutaj zmiany ponizej juz dotycza zmiany zachowania */

.EdroneSelect .multiselect .multiselect__tags-wrap {
  overflow-x: scroll;
  flex-wrap: nowrap;
}

/* Hide scrollbar for Chrome, Safari and Opera */
.EdroneSelect .multiselect .multiselect__tags-wrap::-webkit-scrollbar {
  display: none;
}

/* Hide scrollbar for IE, Edge and Firefox */
.EdroneSelect .multiselect .multiselect__tags-wrap {
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
}

.EdroneSelect .multiselect .multiselect__tags {
  white-space: nowrap;
}

.EdroneSelect .multiselect--active .multiselect__tags {
  border: 1px solid !important;
  @apply tw:border-blue-500;
}

.EdroneSelect .multiselect--active .multiselect__select {
  transform: rotate(180deg);
}

.EdroneSelect--invalid .multiselect .multiselect__tags {
  @apply tw:!border tw:!border-red-500;
}

.EdroneSelect--disabled {
  pointer-events: none;
}

.EdroneSelect--disabled .multiselect .multiselect__tags {
  @apply tw:border tw:border-default-100 tw:bg-default-100 tw:text-default-700;
}

.EdroneSelect--disabled .multiselect .multiselect__single {
  @apply tw:text-default-800;
}

.EdroneSelect--disabled .multiselect .multiselect__select:before {
  @apply tw:border-t-default-300 tw:border-transparent tw:text-default-300;
}

.EdroneSelect--disabled .multiselect .multiselect__spinner:after,
.EdroneSelect--disabled .multiselect .multiselect__spinner:before {
  @apply tw:border-default-300 tw:border-transparent;
}

.EdroneSelect--disabled .multiselect .multiselect__spinner {
  @apply tw:bg-default-100;
}

.multiselect--above .multiselect__content-wrapper {
  bottom: 100%;
  margin-bottom: 8px;
  margin-top: 0;
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-box-shadow: 0 0 0px 1000px #ffffff inset !important;
}
</style>
